import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import throttle from 'lodash/throttle';
import useResponsive from '@dealroadshow/uikit-hooks/useResponsive';
import { setCssVariablesToRoot } from '@framework/dom/setCssVariablesToRoot';
import { getContainer } from '@framework/di/getContainer';
import { useFeatureToggleContext } from '@framework/ui/Organisms/FeatureToggle/Context';
import { ASK_AI } from '@creditflowresearch/config/featureToggle';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import AiSidebarRepository from '@/creditflowresearch/infrastructure/repository/AiSidebarRepository';
import { IAiSidebarState } from '@/creditflowresearch/domain/vo/AiSidebar';
import { clampSidebarWidth, getSplitMinViewport, limitSidebarWidth, SIDEBAR_MIN_WIDTH } from './constants';

const lacksSplitRoom = ({ width }: { width: number }): boolean => width < getSplitMinViewport();

const useAiSidebar = (initialState: IAiSidebarState) => {
  const isSplit = !useResponsive(lacksSplitRoom);
  const { getFeatureByName } = useFeatureToggleContext();
  const { checkAccessByUserPlan } = useUserPlanContext();
  const canOpen = getFeatureByName(ASK_AI).isEnabled && checkAccessByUserPlan(UserPlanType.PREMIUM);
  const [isOpen, setIsOpen] = useState(canOpen && initialState.isOpen);
  const [isResizing, setIsResizing] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [openWidth, setOpenWidth] = useState(() => limitSidebarWidth(initialState.width ?? SIDEBAR_MIN_WIDTH));
  const draggedWidthRef = useRef<number | null>(null);
  const aiSidebarRepository = getContainer().get(AiSidebarRepository);

  const width = isSplit && isOpen && !isFullScreen ? openWidth : 0;

  useEffect(() => {
    setCssVariablesToRoot({
      'cfr-sidebar-width': `${ width }px`,
      'cfr-sidebar-transition': isResizing ? '0s' : '',
    });
  }, [width, isResizing]);

  useEffect(() => {
    aiSidebarRepository.getSidebarState().then((savedState) => {
      if (savedState.width) {
        setOpenWidth(clampSidebarWidth(savedState.width, window.innerWidth));
      }

      if (canOpen && savedState.isOpen) {
        setIsOpen(true);
      }
    });
  }, [canOpen, aiSidebarRepository]);

  useEffect(() => {
    const handleResize = throttle(() => setOpenWidth((value) => clampSidebarWidth(value, window.innerWidth)), 100);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  const saveState = useCallback((state: Partial<IAiSidebarState>): void => {
    aiSidebarRepository.setSidebarState({ isOpen, width: openWidth, ...state });
  }, [isOpen, openWidth, aiSidebarRepository]);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsFullScreen(false);
    saveState({ isOpen: false });
  }, [saveState]);

  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
    saveState({ isOpen: !isOpen });
  }, [isOpen, saveState]);

  const toggleFullScreen = useCallback(() => setIsFullScreen((value) => !value), []);

  const startResizing = useCallback(() => setIsResizing(true), []);
  const stopResizing = useCallback(() => {
    setIsResizing(false);

    if (draggedWidthRef.current) {
      saveState({ width: draggedWidthRef.current });
      draggedWidthRef.current = null;
    }
  }, [saveState]);

  const resizeTo = useCallback((next: number) => {
    const nextWidth = clampSidebarWidth(next, window.innerWidth);

    setOpenWidth(nextWidth);

    if (isResizing) {
      draggedWidthRef.current = nextWidth;
      return;
    }

    saveState({ width: nextWidth });
  }, [isResizing, saveState]);

  const resizeBy = useCallback((step: number) => {
    resizeTo(openWidth + step);
  }, [openWidth, resizeTo]);

  return useMemo(
    () => ({
      isSplit,
      isOpen,
      isFullScreen,
      width,
      close,
      toggle,
      toggleFullScreen,
      startResizing,
      stopResizing,
      resizeTo,
      resizeBy,
    }),
    [
      isSplit, isOpen, isFullScreen, width,
      close, toggle, toggleFullScreen, startResizing, stopResizing, resizeTo, resizeBy,
    ],
  );
};

export default useAiSidebar;
